<template>
  <div>
    <Loader :visible="isLoading" text="Caricamento dati Ordine di Produzione..." />

    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6 space-y-6">
      <p class="text-xl font-semibold text-gray-800">Consuntiva produzione</p>
      <!-- Ricerca MO -->
      <div class="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
        <label class="block text-sm font-medium text-gray-700 mb-2">Numero Ordine di Produzione</label>
        <div class="flex flex-wrap items-center gap-2">
          <input
            v-model="moCode"
            type="text"
            placeholder="Inserisci numero ODP"
            class="w-64 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keyup.enter="caricaMo"
          />
          <PrimaryButton @click="caricaMo" :disabled="!moCode || isLoading">
            <i class="fa fa-search mr-2"></i>Carica ODP
          </PrimaryButton>
          <SecondaryButton v-if="moInfo" @click="reset">
            <i class="fa fa-times mr-2"></i>Reset
          </SecondaryButton>
        </div>
        <p v-if="moError" class="mt-2 text-sm text-red-600">{{ moError }}</p>
      </div>

      <!-- Card info MO -->
      <div v-if="moInfo" class="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-700">
            <i class="fa fa-industry mr-2 text-blue-500"></i>
            Dettaglio ODP — <span>{{ moInfo.MONo }}</span>
          </h3>
          <div class="flex items-center gap-2">
            <a
              :href="route('modules.gp.stampaQrOdp', { mo: currentMo })"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap"
            >
              <i class="fa fa-qrcode"></i>Stampa QR
            </a>
            <span
              class="text-xs px-2 py-1 rounded font-semibold"
              :class="moStatusClass(moInfo.MOStatus)"
            >{{ moStatusLabel(moInfo.MOStatus) }}</span>
          </div>
        </div>
        <div class="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <div v-if="moInfo.Job" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Commessa</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800 font-mono">{{ moInfo.Job }}</span>
          </div>
          <div v-if="moInfo.InternalOrdNo" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Ordine cliente</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800 font-mono">{{ moInfo.InternalOrdNo }}</span>
          </div>
          <div v-if="moInfo.CompanyName" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Cliente</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800">{{ moInfo.CompanyName }}</span>
          </div>
          <div v-if="moInfo.ProductionPlanNo" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Piano di produzione</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800 font-mono">{{ moInfo.ProductionPlanNo }} / {{ moInfo.ProductionPlanLine }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 block">Distinta base</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800 font-mono">{{ moInfo.BOM }}</span>
          </div>
          <div v-if="moInfo.ProductionLotNumber" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Lotto di produzione</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800 font-mono">{{ moInfo.ProductionLotNumber }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 block">Qtà da produrre</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800">{{ formatQty(moInfo.ProductionQty) }} {{ moInfo.UoM }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 block">Qtà prodotta</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800">{{ formatQty(moInfo.ProducedQty) }} {{ moInfo.UoM }}</span>
          </div>
          <div v-if="moInfo.ScrapQuantity > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 block">Qtà scarti</span>
            <span class="flex-1 flex items-center font-semibold text-red-800">{{ formatQty(moInfo.ScrapQuantity) }} {{ moInfo.UoM }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 block">
              Qtà producibile totale (residua)
            </span>
            <span class="flex-1 flex items-center font-semibold" :class="moInfo.MaxProducibleQty > (moInfo.ProducedQty + moInfo.ScrapQuantity) ? 'text-green-700' : 'text-orange-600'">{{ formatQty(moInfo.MaxProducibleQty) }} ({{ formatQty(moInfo.MaxProducibleQty - moInfo.ProducedQty - moInfo.ScrapQuantity) }}) {{ moInfo.UoM }}
              <Link
                v-if="canOpenPrelievo"
                class="inline-flex items-center justify-center w-6 h-6 rounded text-blue-600 hover:bg-blue-100 ml-2"
                :href="route('modules.gp.prelievo', { mono: currentMo })"
              >
                <v-icon name="fa-dolly" title="Apri buono di prelievo" />
              </Link>
            </span>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 block">Data consegna</span>
            <span class="flex-1 flex items-center font-semibold text-gray-800">{{ moInfo.DeliveryDate?.substring(0, 10) }}</span>
          </div>
        </div>
        <div v-if="!isConfirmable(moInfo)" class="px-4 py-2 bg-red-50 border-t border-red-200 flex items-center gap-2 text-sm text-red-700">
          <i class="fa fa-ban"></i>
          La consuntivazione non è consentita: l'ordine di produzione deve essere in stato <strong>Lanciato</strong> o <strong>In lavorazione</strong>.
        </div>
        <div v-if="moInfo.MOStatus == MagoEnums.Production_progress.Created" class="px-4 py-2 bg-amber-50 border-t border-amber-200 flex items-center justify-between gap-2 text-sm">
          <span class="text-amber-700"><i class="fa fa-rocket mr-1"></i>L'ODP è in stato <strong>Creato</strong>: puoi lanciarlo in produzione.</span>
          <button
            @click="lanciaOdp"
            :disabled="isLanciando"
            class="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-green-600 hover:bg-green-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span v-if="isLanciando" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
            <i v-else class="fa fa-rocket"></i>
            {{ isLanciando ? 'Lancio in corso...' : 'Lancia ODP' }}
          </button>
        </div>
      </div>

      <!-- Sezione Fasi ODP -->
      <div v-if="moSteps.length" class="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h3 class="text-sm font-semibold text-gray-700">
            <i class="fa fa-sitemap mr-2 text-blue-500"></i>
            Fasi ODP — {{ currentMo }}
          </h3>
          <div class="flex items-center gap-3 flex-wrap">
            <span v-if="isPolling || confirmationStatus !== null" class="text-xs px-2 py-1 rounded font-semibold" :class="confirmationStatusClass(confirmationStatus)">
              <span v-if="isPolling" class="inline-block animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-current mr-1"></span>
              {{ isPolling ? 'In elaborazione...' : confirmationStatusLabel(confirmationStatus) }}
            </span>
            <span v-if="blockedStepsCount > 0" class="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-semibold">
              <i class="fa fa-ban mr-1"></i>{{ blockedStepsCount }} fase/i non assegnata/e
            </span>
            <span class="text-xs text-gray-500">{{ moSteps.length }} fase/i totali</span>
          </div>
        </div>

        <!-- Tabella DESKTOP -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Stato</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Fase</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Operazione</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">CdL</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">CdL consuntivo</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Tipo</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Data inizio</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Data fine</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Attrezzaggio</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Lav. prevista</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Lav. consuntiva</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Coda</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Avanzamento</th>
                <th class="px-3 py-2 text-center font-medium text-gray-600 border-b">Azione</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="step in moSteps"
                :key="step.RtgStep"
                :class="[
                  'border-b last:border-b-0 transition',
                  step.blocked ? 'bg-gray-100 text-gray-400' : 'bg-white hover:bg-blue-50'
                ]"
              >
                <td class="px-3 py-2">
                  <span class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="moStatusClass(step.MOStatus)">
                    {{ moStatusLabel(step.MOStatus) }}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold"
                    :class="step.blocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-200 text-gray-700'"
                  >
                    {{ step.RtgStep }}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span :class="step.blocked ? 'text-gray-400' : 'text-gray-700'">{{ step.Operation || '—' }}</span>
                </td>
                <td class="px-3 py-2 font-mono text-xs" :class="step.blocked ? 'text-gray-400' : 'text-gray-800'">{{ step.WC }}</td>
                <td class="px-3 py-2 font-mono text-xs" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ step.ActualWC || '—' }}</td>
                <td class="px-3 py-2">
                  <span v-if="step.Outsourced == 1" class="inline-block px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 font-semibold">Esterna</span>
                  <span v-else class="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-semibold">Interna</span>
                  <span v-if="step.Outsourced == 1 && step.SupplierName" class="ml-1 text-xs text-gray-600">{{ step.SupplierName }}</span>
                </td>
                <td class="px-3 py-2 text-xs" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ step.StartingDate ? step.StartingDate.substring(0, 10) : '—' }}</td>
                <td class="px-3 py-2 text-xs" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ step.EndingDate ? step.EndingDate.substring(0, 10) : '—' }}</td>
                <td class="px-3 py-2 text-right" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ formatTime(step.SetupTime) }}</td>
                <td class="px-3 py-2 text-right" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ formatTime(step.ProcessingTime) }}</td>
                <td class="px-3 py-2 text-right font-semibold" :class="step.blocked ? 'text-gray-400' : 'text-blue-600'">{{ step.ActualProcessingTime != null ? formatTime(step.ActualProcessingTime) : '—' }}</td>
                <td class="px-3 py-2 text-right" :class="step.blocked ? 'text-gray-400' : 'text-gray-600'">{{ formatTime(step.QueueTime) }}</td>
                <td class="px-3 py-2">
                  <div class="flex items-center justify-end gap-2">
                    <div class="w-16 bg-gray-200 rounded-full h-1.5">
                      <div
                        class="h-1.5 rounded-full transition-all"
                        :class="stepProgressClass(step)"
                        :style="{ width: Math.min(100, stepProgressPercent(step)) + '%' }"
                      ></div>
                    </div>
                    <span class="text-xs whitespace-nowrap" :class="[stepProgressPercent(step) > 100 ? 'text-red-600 font-semibold' : 'text-gray-500', step.blocked ? 'text-gray-400' : '']">
                      ({{ formatQty(step.ProducedQty) }}/{{ formatQty(step.ProductionQty) }})
                    </span>
                  </div>
                </td>
                <td class="px-3 py-2 text-center">
                  <button
                    v-if="!step.blocked && isConfirmable(step) && isCdlConsuntivabile(step) && step.Outsourced != 1"
                    @click="openConfirmModal(step)"
                    :disabled="isPolling || isSubmitting"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    :class="workerTimeMode >= 3 && activeTimers[step.RtgStep] ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'"
                    :title="'Consuntiva fase ' + step.RtgStep"
                  >
                    <i v-if="workerTimeMode >= 3 && activeTimers[step.RtgStep]" class="fa fa-stopwatch"></i>
                    <i v-else class="fa fa-check"></i>
                    {{ workerTimeMode >= 3 && activeTimers[step.RtgStep] ? 'Timer attivo' : 'Consuntiva' }}
                  </button>
                  <span
                    v-else-if="!step.blocked && isConfirmable(step) && !isCdlConsuntivabile(step) && step.Outsourced != 1"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700 font-semibold"
                  >
                    <i class="fa fa-exclamation-triangle"></i> CDL non consuntivabile
                    <v-icon
                      name="bi-info-circle"
                      scale="0.9"
                      class="cursor-help"
                      :title="'Impostare Consuntiva da a \'Raccolta dati\' per il centro di lavoro ' + (step.ActualWC || step.WC)"
                    />
                  </span>
                  <span
                    v-else-if="!step.blocked && isConfirmable(step) && isCdlConsuntivabile(step) && step.Outsourced == 1"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700 font-semibold"
                  >
                    <i class="fa fa-exclamation-triangle"></i> CDL non consuntivabile
                    <v-icon
                      name="bi-info-circle"
                      scale="0.9"
                      class="cursor-help"
                      :title="'Impossibile consuntivare lavorazione su ' + (step.ActualWC || step.WC) + ' perché esternalizzata.'"
                    />
                  </span>
                  <button
                    v-else-if="!step.blocked && step.MOStatus == MagoEnums.Production_progress.Confirmed && workerTimeMode > 0"
                    @click="openHistoryModal(step)"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded font-semibold transition bg-green-100 text-green-700 hover:bg-green-200"
                    :title="'Storico fase ' + step.RtgStep"
                  >
                    <i class="fa fa-history"></i> Storico
                  </button>
                  <span
                    v-else-if="!step.blocked && requireSequentialSteps && isStepStatusConfirmable(step) && !isPreviousStepConfirmed(step)"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-orange-100 text-orange-700 font-semibold"
                    title="La fase precedente non è ancora terminata"
                  >
                    <i class="fa fa-lock"></i> Attesa fase prec.
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Card MOBILE -->
        <div class="md:hidden divide-y divide-gray-100">
          <div
            v-for="step in moSteps"
            :key="step.RtgStep"
            :class="['p-4 transition', step.blocked ? 'bg-gray-100' : 'bg-white hover:bg-gray-50']"
          >
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold"
                  :class="step.blocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-200 text-gray-700'"
                >
                  {{ step.RtgStep }}
                </span>
                <span :class="['font-semibold text-sm', step.blocked ? 'text-gray-400' : 'text-gray-800']">{{ step.Operation || '—' }}</span>
                <span v-if="step.blocked" class="inline-block px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-600 font-semibold"><i class="fa fa-ban mr-1"></i>Non assegnata</span>
              </div>
              <span class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="moStatusClass(step.MOStatus)">
                {{ moStatusLabel(step.MOStatus) }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span><i class="fa fa-industry mr-1"></i>CdL: <span class="font-mono text-gray-700">{{ step.WC }}</span></span>
              <span v-if="step.ActualWC"><i class="fa fa-check mr-1"></i>Consuntivo: <span class="font-mono text-gray-700">{{ step.ActualWC }}</span></span>
              <span>
                <span v-if="step.Outsourced == 1" class="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">Esterna</span>
                <span v-else class="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold">Interna</span>
              </span>
              <span v-if="step.StartingDate"><i class="fa fa-calendar mr-1"></i>{{ step.StartingDate.substring(0, 10) }}</span>
            </div>
            <div class="mt-2 grid grid-cols-4 gap-2 text-xs text-center">
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Attrezzaggio</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.SetupTime) }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Lav. prevista</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.ProcessingTime) }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Lav. consuntiva</div>
                <div class="font-bold text-blue-600">{{ step.ActualProcessingTime != null ? formatTime(step.ActualProcessingTime) : '—' }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Coda</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.QueueTime) }}</div>
              </div>
            </div>
            <div v-if="!step.blocked && isConfirmable(step) && isCdlConsuntivabile(step) && step.Outsourced != 1" class="mt-3">
              <button
                @click="openConfirmModal(step)"
                :disabled="isPolling || isSubmitting"
                class="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                :class="workerTimeMode >= 3 && activeTimers[step.RtgStep] ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'"
              >
                <i v-if="workerTimeMode >= 3 && activeTimers[step.RtgStep]" class="fa fa-stopwatch"></i>
                <i v-else class="fa fa-check"></i>
                {{ workerTimeMode >= 3 && activeTimers[step.RtgStep] ? 'Timer attivo — Finalizza' : 'Consuntiva fase' }}
              </button>
            </div>
            <div v-else-if="!step.blocked && isConfirmable(step) && !isCdlConsuntivabile(step) && step.Outsourced != 1" class="mt-3">
              <span class="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded bg-yellow-100 text-yellow-700 font-semibold">
                <i class="fa fa-exclamation-triangle"></i> CDL non consuntivabile
                <v-icon
                  name="bi-info-circle"
                  scale="0.9"
                  class="cursor-help"
                  :title="'Impostare Consuntiva da a \'Raccolta dati\' per il centro di lavoro ' + (step.ActualWC || step.WC)"
                />
              </span>
            </div>
            <div v-else-if="!step.blocked && isConfirmable(step) && isCdlConsuntivabile(step) && step.Outsourced == 1" class="mt-3">
              <span class="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded bg-yellow-100 text-yellow-700 font-semibold">
                <i class="fa fa-exclamation-triangle"></i> CDL non consuntivabile
                <v-icon
                  name="bi-info-circle"
                  scale="0.9"
                  class="cursor-help"
                  :title="'Impossibile consuntivare lavorazione su ' + (step.ActualWC || step.WC) + ' perché esternalizzata.'"
                />
              </span>
            </div>
            <div v-else-if="!step.blocked && step.MOStatus == MagoEnums.Production_progress.Confirmed && workerTimeMode > 0" class="mt-3">
              <button
                @click="openHistoryModal(step)"
                class="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded font-semibold transition bg-green-100 text-green-700 hover:bg-green-200"
              >
                <i class="fa fa-history"></i> Storico registrazioni
              </button>
            </div>
            <div v-else-if="!step.blocked && requireSequentialSteps && isStepStatusConfirmable(step) && !isPreviousStepConfirmed(step)" class="mt-3">
              <span class="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded bg-orange-100 text-orange-700 font-semibold">
                <i class="fa fa-lock"></i> Attesa fase precedente
              </span>
            </div>
            <div class="mt-2 flex items-center gap-2">
              <div class="flex-1 bg-gray-200 rounded-full h-1.5">
                <div
                  class="h-1.5 rounded-full transition-all"
                  :class="stepProgressClass(step)"
                  :style="{ width: Math.min(100, stepProgressPercent(step)) + '%' }"
                ></div>
              </div>
              <span class="text-xs whitespace-nowrap" :class="stepProgressPercent(step) > 100 ? 'text-red-600 font-semibold' : 'text-gray-500'">
                ({{ formatQty(step.ProducedQty) }}/{{ formatQty(step.ProductionQty) }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="confirmError" class="border border-red-200 rounded-lg px-4 py-3 bg-red-50 text-sm text-red-700 flex items-center gap-2">
        <i class="fa fa-exclamation-triangle"></i>{{ confirmError }}
      </div>

      <!-- Stato vuoto iniziale -->
      <div v-if="!moInfo && !isLoading" class="text-center py-16 text-gray-400">
        <i class="fa fa-industry text-4xl mb-3 block"></i>
        <p class="text-sm">Inserisci un numero ODP e premi <strong>Carica ODP</strong> per visualizzare le fasi di produzione.</p>
      </div>

    </div>

    <!-- Modal Storico Fase Terminata -->
    <div v-if="historyModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">
              Storico fase {{ historyModal.step?.RtgStep }}
            </h3>
            <p class="text-sm text-gray-500">{{ historyModal.step?.Operation || historyModal.step?.WC }}</p>
            <p v-if="historyModal.step?.ProcessingTime != null" class="text-sm mt-1">
              <span class="font-bold text-blue-600">{{ formatTime(historyModal.step.ActualProcessingTime) }}</span>
              <span class="text-gray-400"> consuntivato su </span>
              <span class="font-semibold text-gray-700">{{ formatTime(historyModal.step.ProcessingTime) }}</span>
              <span class="text-gray-400"> previsto</span>
            </p>
          </div>
          <span class="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Terminata</span>
        </div>

        <div v-if="historyLoading" class="flex items-center justify-center py-8 gap-2 text-gray-400 text-sm">
          <span class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></span>
          Caricamento registrazioni...
        </div>
        <div v-else-if="!historyTimes.length" class="text-center py-8 text-sm text-gray-400">
          <i class="fa fa-clock-o text-2xl mb-2 block"></i>
          Nessuna registrazione trovata per questa fase.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-xs border border-gray-200 rounded overflow-hidden">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1.5 text-left font-medium text-gray-600 border-b">Operatore</th>
                <th v-if="workerTimeMode !== 1" class="px-2 py-1.5 text-left font-medium text-gray-600 border-b">Inizio</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-600 border-b">Fine</th>
                <th class="px-2 py-1.5 text-right font-medium text-gray-600 border-b">Durata</th>
                <th class="px-2 py-1.5 text-right font-medium text-gray-600 border-b">Qtà</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in historyTimes" :key="i" class="border-b last:border-b-0 odd:bg-white even:bg-gray-50">
                <td class="px-2 py-1.5 text-gray-700">{{ row.UserName || '—' }}</td>
                <td v-if="workerTimeMode !== 1" class="px-2 py-1.5 font-mono text-gray-700">{{ row.DateStart ? formatDateTime(row.DateStart) : '—' }}</td>
                <td class="px-2 py-1.5 font-mono text-gray-700">{{ row.DateEnd ? formatDateTime(row.DateEnd) : '—' }}</td>
                <td class="px-2 py-1.5 text-right font-mono text-gray-700">{{ row.ProcessingTime ? formatTime(row.ProcessingTime) : '—' }}</td>
                <td class="px-2 py-1.5 text-right font-mono text-gray-700">{{ row.ProducedQty != null ? formatQty(row.ProducedQty) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end mt-4">
          <button
            @click="closeHistoryModal"
            class="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Consuntivazione Fase -->
    <div v-if="confirmModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">
          Consuntivazione fase {{ confirmModal.step?.RtgStep }}
        </h3>
        <p class="text-sm text-gray-500 mb-4">
          {{ confirmModal.step?.Operation || confirmModal.step?.WC }} —
          <span v-if="confirmModal.limitedByPrev">max da fase precedente: <strong>{{ formatQty(confirmModal.maxQty) }} {{ moInfo.UoM }}</strong></span>
          <span v-else-if="allowExceedMaxQty">residuo: <strong>{{ formatQty(confirmModal.maxQty) }} {{ moInfo.UoM }}</strong> <span class="text-amber-600">(superamento consentito)</span></span>
          <span v-else>max. producibile: <strong>{{ formatQty(confirmModal.maxQty) }} {{ moInfo.UoM }}</strong></span>
        </p>

        <div v-if="users.length && $page.props.isAdminOrSuperAdmin" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Operatore</label>
          <select
            v-model="selectedUserId"
            class="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
          </select>
        </div>

        <div v-if="isAdminOnOtherUser && confirmModal.timeMode >= 3" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Modalità inserimento tempo</label>
          <select
            v-model="confirmModal.forcedTimeMode"
            class="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option :value="null">Timer (modalità predefinita)</option>
            <option :value="1">Inserimento manuale (HH:MM:SS)</option>
            <option :value="2">Intervallo date (inizio/fine)</option>
          </select>
        </div>

        <div v-if="workerTimeMode > 0" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Tempo di lavorazione</label>

          <div v-if="confirmModal.timerRunning && effectiveTimeMode >= 3" class="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 mb-3">
            <div class="flex items-center justify-between gap-2 text-orange-700 text-sm font-semibold">
              <div class="flex items-center gap-2">
                <i class="fa fa-stopwatch"></i>
                Timer in corso: <span class="font-mono text-lg">{{ elapsedFromStart(confirmModal.timerStartedAt, confirmModal.timerUseCustomEnd && confirmModal.timerCustomEnd ? new Date(confirmModal.timerCustomEnd).getTime() : null) }}</span>
              </div>
              <button
                type="button"
                @click="confirmModal.timerUseCustomEnd = !confirmModal.timerUseCustomEnd; confirmModal.timerCustomEnd = ''"
                class="text-xs px-2 py-1 rounded bg-orange-200 hover:bg-orange-300 text-orange-800 font-medium transition whitespace-nowrap"
                :title="confirmModal.timerUseCustomEnd ? 'Usa orario attuale' : 'Imposta momento specifico'"
              >
                <i class="fa fa-clock-o mr-1"></i>{{ confirmModal.timerUseCustomEnd ? 'Orario attuale' : 'Correggi timer' }}
              </button>
            </div>
            <div v-if="confirmModal.timerUseCustomEnd" class="mt-2">
              <label class="block text-xs text-orange-600 mb-1">Data/ora di fine (non può essere futura)</label>
              <input
                v-model="confirmModal.timerCustomEnd"
                type="datetime-local"
                :min="timerStartMin"
                :max="timerNowMax"
                class="w-full p-1.5 border border-orange-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white text-gray-700"
              />
            </div>
            <p v-else class="text-xs text-orange-600 mt-0.5">Il tempo verrà registrato automaticamente alla conferma.</p>
          </div>

          <div v-else-if="effectiveTimeMode === 1" class="grid grid-cols-3 gap-2 mb-3">
            <div>
              <label class="block text-xs text-gray-500 mb-1 text-center">Ore</label>
              <input v-model.number="confirmModal.timeHH" type="number" min="0" class="w-full p-2 border border-gray-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="HH" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1 text-center">Minuti</label>
              <input v-model.number="confirmModal.timeMM" type="number" min="0" max="59" @input="confirmModal.timeMM = Math.min(59, Math.max(0, confirmModal.timeMM || 0))" class="w-full p-2 border border-gray-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="MM" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1 text-center">Secondi</label>
              <input v-model.number="confirmModal.timeSS" type="number" min="0" max="59" @input="confirmModal.timeSS = Math.min(59, Math.max(0, confirmModal.timeSS || 0))" class="w-full p-2 border border-gray-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="SS" />
            </div>
          </div>

          <div v-else-if="effectiveTimeMode === 2" class="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label class="block text-xs text-gray-500 mb-1">Data inizio</label>
              <input v-model="confirmModal.dateStart" type="datetime-local" class="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Data fine</label>
              <input v-model="confirmModal.dateEnd" type="datetime-local" class="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div v-else-if="effectiveTimeMode >= 3" class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 mb-3 text-sm text-blue-700">
            <i class="fa fa-info-circle mr-1"></i>
            Clicca <strong>Avvia timer</strong> per registrare l'inizio. Potrai confermare la quantità prodotta al termine.
          </div>

          <div v-if="workerTimesLoading" class="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
            <span class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></span>
            Caricamento registrazioni...
          </div>
          <div v-else-if="workerTimes.length" class="mt-2">
            <p class="text-xs font-medium text-gray-500 mb-1">Registrazioni precedenti</p>
            <table class="w-full text-xs border border-gray-200 rounded overflow-hidden">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-2 py-1 text-left font-medium text-gray-600 border-b">Operatore</th>
                  <th v-if="effectiveTimeMode !== 1" class="px-2 py-1 text-left font-medium text-gray-600 border-b" >Inizio</th>
                  <th v-if="effectiveTimeMode !== 1" class="px-2 py-1 text-left font-medium text-gray-600 border-b">Fine</th>
                  <th v-else="effectiveTimeMode !== 1" class="px-2 py-1 text-left font-medium text-gray-600 border-b">Data</th>
                  <th class="px-2 py-1 text-right font-medium text-gray-600 border-b">Durata</th>
                  <th class="px-2 py-1 text-right font-medium text-gray-600 border-b">Qtà {{ moInfo.UoM }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in workerTimes" :key="i" class="border-b last:border-b-0 odd:bg-white even:bg-gray-50">
                  <td class="px-2 py-1 text-gray-700">{{ row.UserName || '—' }}</td>
                  <td v-if="effectiveTimeMode !== 1" class="px-2 py-1 font-mono text-gray-700">{{ row.DateStart ? formatDateTime(row.DateStart) : '—' }}</td>
                  <td class="px-2 py-1 font-mono text-gray-700">{{ row.DateEnd ? formatDateTime(row.DateEnd) : '—' }}</td>
                  <td class="px-2 py-1 text-right font-mono text-gray-700">{{ formatTime(row.ProcessingTime) }}</td>
                  <td class="px-2 py-1 text-right font-mono text-gray-700">{{ row.ProducedQty != null ? formatQty(row.ProducedQty) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="effectiveTimeMode < 3 || confirmModal.timerRunning" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Quantità prodotta</label>
          <input
            v-model.number="confirmModal.producedQty"
            type="number"
            min="0.001"
            :max="(allowExceedMaxQty && !confirmModal.limitedByPrev) ? null : confirmModal.maxQty"
            step="any"
            class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            @keyup.enter="confirmModal.timerRunning ? submitConfirm() : (effectiveTimeMode < 3 ? submitConfirm() : null)"
          />
          <p v-if="confirmModal.error" class="mt-1 text-xs text-red-600">{{ confirmModal.error }}</p>
        </div>
        <div v-else>
          <p v-if="confirmModal.error" class="mt-1 text-xs text-red-600">{{ confirmModal.error }}</p>
        </div>

        <div v-if="isLastStep && allowScrap && !(effectiveTimeMode >= 3 && !confirmModal.timerRunning)" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Quantità scarti</label>
          <input
            v-model.number="confirmModal.scrapQty"
            type="number"
            min="0"
            step="any"
            class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            @keyup.enter="submitConfirm"
          />
        </div>

        <div v-if="isLastStep && allowLotChangeProduction && !(effectiveTimeMode >= 3 && !confirmModal.timerRunning)" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Lotto di produzione</label>
          <div class="flex gap-2">
            <input
              v-model="confirmModal.productionLot"
              type="text"
              placeholder="Codice lotto"
              :disabled="lotFieldDisabled"
              class="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              @click="generateLot"
              :disabled="confirmModal.productionLotGenerating || lotFieldDisabled"
              class="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Genera lotto"
            >
              <span v-if="confirmModal.productionLotGenerating" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
              <i v-else class="fa fa-magic"></i>
            </button>
          </div>
        </div>

        <div v-if="!(effectiveTimeMode >= 3 && !confirmModal.timerRunning)" class="flex items-center gap-2 mb-4">
          <input id="modal-closed" type="checkbox" v-model="confirmModal.closed" class="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
          <label for="modal-closed" class="text-sm text-gray-700">Chiudi fase</label>
        </div>

        <div v-if="willAlignPreviousSteps()" class="mb-4 flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
          <i class="fa fa-info-circle mt-0.5 shrink-0"></i>
          <span>
            La quantità prodotta totale di questa fase
            (<strong>{{ formatQty(parseFloat(confirmModal.step?.ProducedQty ?? 0) + parseFloat(confirmModal.producedQty ?? 0)) }}</strong>)
            supera quella di una o più fasi precedenti.
            Tutte le fasi precedenti con quantità inferiore verranno <strong>allineate</strong> a questo valore.
          </span>
        </div>

        <div v-if="willTriggerReturn()" class="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <i class="fa fa-exclamation-triangle mt-0.5 shrink-0"></i>
          <span>
            Stai chiudendo l'ultima fase con una quantità prodotta finale inferiore a quella richiesta
            (<strong>{{ formatQty(parseFloat(confirmModal.step?.ProducedQty ?? 0) + parseFloat(confirmModal.producedQty ?? 0)) }}</strong>
            su <strong>{{ formatQty(moInfo.ProductionQty) }}</strong>).
            Verrà effettuato automaticamente un <strong>reso dei materiali</strong> non utilizzati.
          </span>
        </div>

        <div class="flex justify-end gap-3">
          <button
            @click="closeConfirmModal"
            class="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            :disabled="confirmModal.submitting"
          >
            Annulla
          </button>
          <button
            v-if="!confirmModal.timerRunning && effectiveTimeMode >= 3"
            @click="startTimer"
            :disabled="confirmModal.submitting"
            class="px-4 py-2 text-sm rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <span v-if="confirmModal.submitting" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
            <i v-else class="fa fa-play mr-1"></i>
            {{ confirmModal.submitting ? 'Avvio...' : 'Avvia timer' }}
          </button>
          <button
            v-else
            @click="submitConfirm"
            :disabled="!confirmModal.producedQty || confirmModal.producedQty <= 0 || confirmModal.submitting"
            class="px-4 py-2 text-sm rounded text-white disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            :class="confirmModal.timerRunning && effectiveTimeMode >= 3 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'"
          >
            <span v-if="confirmModal.submitting" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
            <template v-else>
              <i v-if="confirmModal.timerRunning && effectiveTimeMode >= 3" class="fa fa-stop mr-1"></i>
              <i v-else class="fa fa-check mr-1"></i>
            </template>
            {{ confirmModal.submitting ? 'Invio...' : (confirmModal.timerRunning && effectiveTimeMode >= 3 ? 'Ferma e Consuntiva' : 'Conferma') }}
          </button>
        </div>
      </div>
    </div>

    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @update:show="toast.show = $event"
    />

  </div>
</template>

<script>
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    BreadCrumbs,
    Loader,
    PrimaryButton,
    SecondaryButton,
    ToastNotification,
    Link,
  },

  props: {
    canOpenPrelievo: {
      type: Boolean,
      default: false,
    },
    allowScrap: {
      type: Boolean,
      default: false,
    },
    allowLotChangeProduction: {
      type: Boolean,
      default: false,
    },
    allowExceedMaxQty: {
      type: Boolean,
      default: false,
    },
    requireSequentialSteps: {
      type: Boolean,
      default: false,
    },
    limitQtyToPrevStep: {
      type: Boolean,
      default: false,
    },
    workerTimeMode: {
      type: Number,
      default: 0,
    },
    lotConfig: {
      type: Object,
      default: () => ({ prefix: '', year_format: 'none', separator: '-', suffix: '' }),
    },
    users: {
      type: Array,
      default: () => [],
    },
    qtyDecimalPrecision: {
      type: Number,
      default: 2,
    },
  },

  data() {
    return {
      moCode: '',
      currentMo: '',
      isLoading: false,
      isSubmitting: false,
      isPolling: false,
      confirmationStatus: null,
      confirmationMessage: null,
      confirmError: null,
      moError: null,
      isLanciando: false,
      moInfo: null,
      moSteps: [],
      toast: { show: false, message: '', type: 'success' },
      activeTimers: {},
      timerNow: Date.now(),
      timerInterval: null,
      workerTimes: [],
      workerTimesLoading: false,
      selectedUserId: null,
      historyModal: {
        open: false,
        step: null,
      },
      historyTimes: [],
      historyLoading: false,
      confirmModal: {
        open: false,
        step: null,
        maxQty: 0,
        limitedByPrev: false,
        producedQty: null,
        scrapQty: null,
        productionLot: '',
        productionLotGenerating: false,
        closed: false,
        submitting: false,
        error: null,
        timeMode: 0,
        timeHH: 0,
        timeMM: 0,
        timeSS: 0,
        dateStart: '',
        dateEnd: '',
        timerRunning: false,
        timerStartedAt: null,
        timerUseCustomEnd: false,
        timerCustomEnd: '',
        forcedTimeMode: null,
      },

      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.gestione-produzione'), href: route('modules.home', 'gestione-produzione') },
        { name: this.$t('modules.consuntiva-produzione'), href: route('modules.gp.consuntiva-produzione') },
      ],
    };
  },

  computed: {
    isAdminOnOtherUser() {
      return this.users.length && this.selectedUserId != null && String(this.selectedUserId) !== String(this.$page.props.auth.user?.id) && this.$page.props.isAdminOrSuperAdmin;
    },
    effectiveTimeMode() {
      if (this.isAdminOnOtherUser && this.confirmModal.forcedTimeMode !== null) {
        return this.confirmModal.forcedTimeMode;
      }
      return this.confirmModal.timeMode;
    },
    blockedStepsCount() {
      return this.moSteps.filter(s => s.blocked).length;
    },
    isLastStep() {
      if (!this.confirmModal.open || !this.confirmModal.step) return false;
      const maxStep = Math.max(...this.moSteps.map(s => s.RtgStep));
      return this.confirmModal.step.RtgStep == maxStep;
    },
    lotFieldDisabled() {
      return parseFloat(this.confirmModal.step?.ProducedQty ?? 0) > 0;
    },
    timerStartMin() {
      const s = this.confirmModal.timerStartedAt;
      if (!s) return '';
      const d = new Date(s);
      d.setSeconds(d.getSeconds() + 1);
      return d.toISOString().slice(0, 16);
    },
    timerNowMax() {
      return new Date().toISOString().slice(0, 16);
    },
  },

  mounted() {
    const params = new URLSearchParams(window.location.search);
    const mono = params.get('mono');
    if (mono) {
      this.moCode = mono;
      this.caricaMo();
    }
  },

  methods: {
    async caricaMo() {
      if (!this.moCode) {
        this.moError = 'Inserisci un numero di MO';
        return;
      }
      this.moError              = null;
      this.confirmError         = null;
      this.isLoading            = true;
      this.currentMo            = this.moCode;
      this.confirmationStatus   = null;
      this.confirmationMessage  = null;

      try {
        const response = await axios.get(route('modules.gp.consuntiva.getMoData'), {
          params: { mo: this.moCode },
        });
        this.moInfo  = response.data.moInfo  || null;
        this.moSteps = response.data.moSteps || [];

        if (!this.moInfo) {
          this.showToast('Ordine di produzione non trovato', 'error');
          return;
        }

        await this.loadActiveTimers();

        const initialStatus = response.data.confirmationStatus ?? null;
        if (initialStatus) {
          const E = this.MagoEnums.EOPENMESACTIONSTATUS;
          this.confirmationStatus  = initialStatus.status;
          this.confirmationMessage = initialStatus.message;
          if (initialStatus.status !== null && initialStatus.status !== E.Done && initialStatus.status !== E.Error) {
            await this.pollConfirmationStatus();
          }
        }
      } catch (error) {
        this.moError  = error?.response?.data?.error || "Errore nel caricamento dell'ordine di produzione";
        this.moInfo   = null;
        this.moSteps  = [];
      } finally {
        this.isLoading = false;
      }
    },

    reset() {
      this.moCode              = '';
      this.currentMo           = '';
      this.moError             = null;
      this.confirmError        = null;
      this.moInfo              = null;
      this.moSteps             = [];
      this.confirmationStatus  = null;
      this.confirmationMessage = null;
      this.activeTimers        = {};
    },

    moStatusLabel(status) {
      const E = this.MagoEnums.Production_progress;
      const map = {
        [E.Released]:                 'Lanciato',
        [E.Processing]:               'In lavorazione',
        [E.Confirmed]:                'Terminato',
        [E.Created]:                  'Creato',
        [E.Proposed_by_MRP]:          'Proposto da MRP',
        [E.Planned_by_CRP]:           'Pianificato da CRP',
        [E.Proposed_by_MRPMinusCRP]:  'Proposto MRP-CRP',
      };
      return map[status] ?? '—';
    },

    moStatusClass(status) {
      const E = this.MagoEnums.Production_progress;
      const map = {
        [E.Released]:                'bg-blue-100 text-blue-700',
        [E.Processing]:              'bg-yellow-100 text-yellow-700',
        [E.Confirmed]:               'bg-green-100 text-green-700',
        [E.Created]:                 'bg-gray-100 text-gray-600',
        [E.Proposed_by_MRP]:         'bg-purple-100 text-purple-700',
        [E.Planned_by_CRP]:          'bg-indigo-100 text-indigo-700',
        [E.Proposed_by_MRPMinusCRP]: 'bg-pink-100 text-pink-700',
      };
      return map[status] ?? 'bg-gray-100 text-gray-500';
    },

    formatTime(val) {
      if (val === null || val === undefined || val === '') return '—';
      const totalSeconds = Number(val);
      if (isNaN(totalSeconds)) return '—';
      const hours   = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return `${hours}:${String(minutes).padStart(2, '0')}`;
    },

    formatQty(val) {
      if (val === null || val === undefined || val === '') return '—';
      const n = Number(val);
      if (isNaN(n)) return '—';
      return n.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: this.qtyDecimalPrecision });
    },

    openConfirmModal(step) {
      const sorted = [...this.moSteps].sort((a, b) => a.RtgStep - b.RtgStep);
      const idx = sorted.findIndex(s => s.RtgStep === step.RtgStep);
      const prevStep = idx > 0 ? sorted[idx - 1] : null;
      const alreadyProduced = parseFloat(step.ProducedQty ?? 0);

      let maxQty;
      let limitedByPrev = false;
      if (this.limitQtyToPrevStep && prevStep) {
        const prevProduced = parseFloat(prevStep.ProducedQty ?? 0);
        maxQty = Math.max(0, prevProduced - alreadyProduced);
        limitedByPrev = true;
      } else {
        const maxProducible = parseFloat(this.moInfo?.MaxProducibleQty ?? 0);
        maxQty = Math.max(0, maxProducible - alreadyProduced);
      }

      const timerStartedAt = this.activeTimers[step.RtgStep] ?? null;
      const timerRunning   = timerStartedAt !== null;

      if (this.users.length && this.$page.props.isAdminOrSuperAdmin && !this.selectedUserId) {
        this.selectedUserId = this.$page.props.auth.user?.id ?? null;
      }

      this.confirmModal = {
        open: true,
        step,
        maxQty,
        limitedByPrev,
        producedQty: maxQty > 0 ? maxQty : null,
        scrapQty: null,
        productionLot: this.moInfo?.ProductionLotNumber || '',
        productionLotGenerating: false,
        closed: false,
        submitting: false,
        error: null,
        timeMode: this.workerTimeMode,
        timeHH: 0,
        timeMM: 0,
        timeSS: 0,
        dateStart: '',
        dateEnd: '',
        timerRunning,
        timerStartedAt,
        timerUseCustomEnd: false,
        timerCustomEnd: '',
        forcedTimeMode: null,
      };

      if (timerRunning) {
        this.timerNow     = Date.now();
        this.timerInterval = setInterval(() => { this.timerNow = Date.now(); }, 1000);
      }

      if (this.workerTimeMode > 0) {
        this.loadWorkerTimes(step);
      }
    },

    closeConfirmModal() {
      this.confirmModal.open  = false;
      this.workerTimes        = [];
      this.workerTimesLoading = false;
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    async loadWorkerTimes(step) {
      this.workerTimes        = [];
      this.workerTimesLoading = true;
      try {
        const res = await axios.get(route('modules.gp.consuntiva.workerTimes'), {
          params: { mo: this.currentMo, rtg_step: step.RtgStep },
        });
        this.workerTimes = res.data || [];
      } catch {
        this.workerTimes = [];
      } finally {
        this.workerTimesLoading = false;
      }
    },

    async submitConfirm() {
      const m = this.confirmModal;
      if (!m.producedQty || m.producedQty <= 0) {
        m.error = 'Inserisci una quantità valida';
        return;
      }
      if ((m.limitedByPrev || !this.allowExceedMaxQty) && m.producedQty > m.maxQty) {
        m.error = `La quantità non può superare ${m.maxQty}`;
        return;
      }
      m.error       = null;
      m.submitting  = true;
      this.confirmError         = null;
      this.confirmationStatus   = null;
      this.confirmationMessage  = null;
      this.isSubmitting         = true;

      try {
        const effectiveMode = this.effectiveTimeMode;
        const payload = {
          mo:             this.currentMo,
          rtg_step:       m.step.RtgStep,
          produced_qty:   m.producedQty,
          scrap_qty:      this.allowScrap ? (m.scrapQty || 0) : 0,
          production_lot: (this.isLastStep && this.allowLotChangeProduction) ? (m.productionLot || '') : '',
          closed:         m.closed,
          time_mode:      effectiveMode,
          ...(this.selectedUserId ? { user_id: this.selectedUserId } : {}),
        };

        if (effectiveMode === 1) {
          payload.time_hh = m.timeHH || 0;
          payload.time_mm = m.timeMM || 0;
          payload.time_ss = m.timeSS || 0;
        } else if (effectiveMode === 2) {
          payload.date_start = m.dateStart;
          payload.date_end   = m.dateEnd;
        } else if (effectiveMode >= 3 && m.timerRunning && m.timerUseCustomEnd && m.timerCustomEnd) {
          payload.timer_date_end = m.timerCustomEnd;
        }

        const response = await axios.post(route('modules.gp.consuntiva.confirmStep'), payload);
        if (response.data?.success === false) {
          m.error = response.data?.error || 'Errore durante la consuntivazione';
          return;
        }
        const maxStep = Math.max(...this.moSteps.map(s => s.RtgStep));
        const isLastStep = m.step.RtgStep == maxStep && m.closed;
        if (effectiveMode >= 3 && m.step) {
          const timers = { ...this.activeTimers };
          delete timers[m.step.RtgStep];
          this.activeTimers = timers;
        }
        this.closeConfirmModal();
        await this.pollConfirmationStatus(isLastStep);
      } catch (error) {
        m.error = error?.response?.data?.error || 'Errore durante la consuntivazione';
      } finally {
        m.submitting  = false;
        this.isSubmitting = false;
      }
    },

    async pollConfirmationStatus(isLastStep = false) {
      this.isPolling = true;
      const maxAttempts = 30;

      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));

        try {
          const res = await axios.get(route('modules.gp.consuntiva.confirmationStatus'), {
            params: { mo: this.currentMo },
          });

          this.confirmationStatus  = res.data.status;
          this.confirmationMessage = res.data.message;

          const E = this.MagoEnums.EOPENMESACTIONSTATUS;
          if (res.data.status === E.Done) {
            this.showToast('Consuntivazione completata con successo', 'success');
            await this.caricaMo();
            break;
          }
          if (isLastStep && res.data.status === null && (res.data.message === '' || res.data.message === null)) {
            this.confirmationStatus = E.Done;
            this.showToast('Consuntivazione completata con successo', 'success');
            await this.caricaMo();
            break;
          }
          if (res.data.status === E.Error) {
            this.confirmError = res.data.message || 'Errore nella consuntivazione';
            break;
          }
        } catch {
          break;
        }
      }

      this.isPolling = false;
    },

    async lanciaOdp() {
      this.isLanciando = true;
      try {
        const response = await axios.post(route('modules.gp.lanciaOdp'), { mo: this.currentMo });
        if (response.data?.success === false) {
          this.showToast(response.data.error || 'Errore durante il lancio', 'error');
          return;
        }
        this.showToast('ODP lanciato in produzione con successo', 'success');
        await this.caricaMo();
      } catch (error) {
        this.showToast(error?.response?.data?.error || 'Errore durante il lancio', 'error');
      } finally {
        this.isLanciando = false;
      }
    },

    isConfirmable(stepOrMo) {
      const E = this.MagoEnums.Production_progress;
      const statusOk = stepOrMo.MOStatus == E.Released || stepOrMo.MOStatus == E.Processing;
      if (!statusOk) return false;
      if (stepOrMo.RtgStep == null) return true;
      return this.isPreviousStepConfirmed(stepOrMo);
    },

    isCdlConsuntivabile(step) {
      const confirmType = step.ActualWC ? step.C2 : step.C1;
      return confirmType == this.MagoEnums.MO_Confirmation_Type.Data_Collection;
    },

    isPreviousStepConfirmed(step) {
      if (!this.requireSequentialSteps) return true;
      const sorted = [...this.moSteps].sort((a, b) => a.RtgStep - b.RtgStep);
      const idx = sorted.findIndex(s => s.RtgStep === step.RtgStep);
      if (idx <= 0) return true;
      const prev = sorted[idx - 1];
      return prev.MOStatus == this.MagoEnums.Production_progress.Confirmed;
    },

    isStepStatusConfirmable(step) {
      const E = this.MagoEnums.Production_progress;
      return step.MOStatus == E.Released || step.MOStatus == E.Processing;
    },

    confirmationStatusLabel(status) {
      if (status === null) return '';
      const E = this.MagoEnums.EOPENMESACTIONSTATUS;
      const map = {
        [E.To_Do]:        'In attesa',
        [E.In_Processing]:'In elaborazione',
        [E.Done]:         'Completata',
        [E.Error]:        'Errore',
        [E.Wip]:          'In corso',
        [E.Hold]:         'In attesa',
      };
      return map[status] ?? '';
    },

    confirmationStatusClass(status) {
      if (status === null) return 'bg-yellow-100 text-yellow-700';
      const E = this.MagoEnums.EOPENMESACTIONSTATUS;
      const map = {
        [E.To_Do]:        'bg-gray-100 text-gray-600',
        [E.In_Processing]:'bg-yellow-100 text-yellow-700',
        [E.Done]:         'bg-green-100 text-green-700',
        [E.Error]:        'bg-red-100 text-red-700',
        [E.Wip]:          'bg-yellow-100 text-yellow-700',
        [E.Hold]:         'bg-gray-100 text-gray-600',
      };
      return map[status] ?? 'bg-yellow-100 text-yellow-700';
    },

    stepProgressPercent(step) {
      const prod = parseFloat(step.ProductionQty);
      if (!prod) return 0;
      return Math.round((parseFloat(step.ProducedQty) / prod) * 100);
    },

    stepProgressClass(step) {
      const pct = this.stepProgressPercent(step);
      if (pct >= 100) return 'bg-green-500';
      if (pct > 0)    return 'bg-blue-500';
      return 'bg-gray-300';
    },

    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type    = type;
      this.toast.show    = true;
    },

    willTriggerReturn() {
      const m = this.confirmModal;
      if (!m.open || !m.closed || !m.step || !this.moInfo) return false;

      const maxStep = Math.max(...this.moSteps.map(s => s.RtgStep));
      if (m.step.RtgStep != maxStep) return false;

      const alreadyProduced = parseFloat(m.step.ProducedQty ?? 0);
      const confirming      = parseFloat(m.producedQty ?? 0);
      const productionQty   = parseFloat(this.moInfo.ProductionQty ?? 0);

      return (alreadyProduced + confirming) < productionQty;
    },

    willAlignPreviousSteps() {
      const m = this.confirmModal;
      if (!m.open || !m.step) return false;

      const total = parseFloat(m.step.ProducedQty ?? 0) + parseFloat(m.producedQty ?? 0);
      if (total <= 0) return false;

      const sorted = [...this.moSteps].sort((a, b) => a.RtgStep - b.RtgStep);
      const idx    = sorted.findIndex(s => s.RtgStep === m.step.RtgStep);
      if (idx <= 0) return false;

      return sorted.slice(0, idx).some(s => parseFloat(s.ProducedQty ?? 0) < total);
    },

    buildLotSearchPattern() {
      const c = this.lotConfig;
      let prefix = (c.prefix || '').trim();
      if (c.year_format === '2-digit') {
        prefix += new Date().getFullYear().toString().slice(-2);
      } else if (c.year_format === '4-digit') {
        prefix += new Date().getFullYear().toString();
      }
      const sep = (c.separator && c.separator !== 'none') ? c.separator : '';
      if (prefix.length > 0 && sep) prefix += sep;
      const suffix = (c.suffix || '').trim();
      return { prefix, suffix };
    },

    async generateLot() {
      const m = this.confirmModal;
      m.productionLotGenerating = true;
      try {
        const pattern = this.buildLotSearchPattern();
        const item    = this.moInfo?.BOM || '';
        const res = await axios.post(route('modules.gp.consuntiva.getNextLot'), {
          prefix: pattern.prefix,
          suffix: pattern.suffix,
          item,
        });
        if (res.data?.success) {
          m.productionLot = res.data.lot;
        } else {
          this.showToast('Errore nella generazione del lotto', 'error');
        }
      } catch {
        this.showToast('Errore nella generazione del lotto', 'error');
      } finally {
        m.productionLotGenerating = false;
      }
    },

    async loadActiveTimers(userId = null) {
      if (!this.currentMo || this.workerTimeMode < 3) return;
      try {
        const params = { mo: this.currentMo };
        if (userId) params.user_id = userId;
        const res = await axios.get(route('modules.gp.consuntiva.activeTimers'), { params });
        this.activeTimers = res.data || {};
      } catch {
        this.activeTimers = {};
      }
    },

    async startTimer() {
      const m = this.confirmModal;
      m.submitting = true;
      try {
        const res = await axios.post(route('modules.gp.consuntiva.startTimer'), {
          mo:        this.currentMo,
          rtg_step:  m.step.RtgStep,
          time_mode: this.workerTimeMode,
          ...(this.selectedUserId ? { user_id: this.selectedUserId } : {}),
        });
        if (res.data?.success) {
          this.activeTimers = { ...this.activeTimers, [m.step.RtgStep]: res.data.dateStart };
          this.showToast('Timer avviato', 'success');
          this.closeConfirmModal();
        } else {
          m.error = 'Errore nell\'avvio del timer';
        }
      } catch (error) {
        m.error = error?.response?.data?.error || 'Errore nell\'avvio del timer';
      } finally {
        m.submitting = false;
      }
    },

    async openHistoryModal(step) {
      this.historyModal  = { open: true, step };
      this.historyTimes  = [];
      this.historyLoading = true;
      try {
        const res = await axios.get(route('modules.gp.consuntiva.workerTimesAll'), {
          params: { mo: this.currentMo, rtg_step: step.RtgStep },
        });
        this.historyTimes = res.data || [];
      } catch {
        this.historyTimes = [];
      } finally {
        this.historyLoading = false;
      }
    },

    closeHistoryModal() {
      this.historyModal  = { open: false, step: null };
      this.historyTimes  = [];
      this.historyLoading = false;
    },

    elapsedFromStart(isoStart, endMs = null) {
      if (!isoStart) return '00:00:00';
      const startMs  = new Date(isoStart).getTime();
      const elapsedS = Math.max(0, Math.floor(((endMs ?? this.timerNow) - startMs) / 1000));
      const hh = Math.floor(elapsedS / 3600);
      const mm = Math.floor((elapsedS % 3600) / 60);
      const ss = elapsedS % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    },
  },

  watch: {
    async selectedUserId(newId) {
      if (!this.confirmModal.open || this.workerTimeMode < 3 || !this.confirmModal.step) return;

      this.confirmModal.forcedTimeMode = null;

      await this.loadActiveTimers(newId);

      const timerStartedAt = this.activeTimers[this.confirmModal.step.RtgStep] ?? null;
      const timerRunning   = timerStartedAt !== null;

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      this.confirmModal.timerRunning   = timerRunning;
      this.confirmModal.timerStartedAt = timerStartedAt;

      if (timerRunning) {
        this.timerNow      = Date.now();
        this.timerInterval = setInterval(() => { this.timerNow = Date.now(); }, 1000);
      }
    },
  },
};
</script>
